import {
    FunctionComponent,
    ReactElement,
    SyntheticEvent,
    useMemo,
    useState,
} from "react";

import { Autocomplete, CircularProgress, TextField } from "@mui/material";

import { useCreateIngredient, useGetIngredients } from "../../hooks";

export interface IIngredientOption {
    id: string;
    label: string;
}

interface ISelectableOption extends IIngredientOption {
    isNew?: boolean;
}

export interface IIngredientAutocompleteProps {
    value: IIngredientOption | null;
    onChange: (value: IIngredientOption | null) => void;
    error?: boolean;
    helperText?: string;
}

export const IngredientAutocomplete: FunctionComponent<
    IIngredientAutocompleteProps
> = (props: IIngredientAutocompleteProps): ReactElement => {
    const { value, onChange, error, helperText } = props;
    const [inputValue, setInputValue] = useState(value?.label ?? "");

    const ingredientsQuery = useGetIngredients(
        ["ingredients", inputValue],
        { q: inputValue || undefined, page: 1, limit: 20 },
        { keepPreviousData: true },
    );
    const createIngredientMutation = useCreateIngredient("ingredients");

    const options: ISelectableOption[] = useMemo(() => {
        const fetched: ISelectableOption[] = (
            ingredientsQuery.data?.records ?? []
        ).map((ingredient) => ({ id: ingredient.id, label: ingredient.name }));
        const trimmed = inputValue.trim();
        const exactMatch = fetched.some(
            (option) => option.label.toLowerCase() === trimmed.toLowerCase(),
        );

        if (trimmed && !exactMatch) {
            return [
                ...fetched,
                { id: "__create__", label: `Add "${trimmed}"`, isNew: true },
            ];
        }

        return fetched;
    }, [ingredientsQuery.data, inputValue]);

    const handleChange = async (
        _event: SyntheticEvent,
        selected: ISelectableOption | null,
    ): Promise<void> => {
        if (!selected) {
            onChange(null);
            return;
        }

        if (selected.isNew) {
            const created = await createIngredientMutation.mutateAsync({
                name: inputValue.trim(),
            });
            onChange({ id: created.id, label: created.name });
            return;
        }

        onChange({ id: selected.id, label: selected.label });
    };

    return (
        <Autocomplete
            size="small"
            fullWidth
            options={options}
            loading={ingredientsQuery.isFetching}
            value={value}
            isOptionEqualToValue={(option, selected) =>
                option.id === selected.id
            }
            getOptionLabel={(option) => option.label}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) =>
                setInputValue(newInputValue)
            }
            onChange={handleChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Ingredient"
                    placeholder="Search or add an ingredient"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {ingredientsQuery.isFetching ||
                                createIngredientMutation.isLoading ? (
                                    <CircularProgress size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
